import { useEffect, useState } from 'react';

type UsePaginationResult<T> = {
    currentPage: number;
    setCurrentPage: (page: number) => void;
    currentItems: T[];
    totalPages: number;
    startIndex: number;
    endIndex: number;
    itemsPerPage: number;
};

/**
 * Custom hook for managing pagination state and calculations
 * @param items - Array of items to paginate
 * @param itemsPerPage - Number of items per page (default: 25)
 * @returns Pagination state and calculated values
 */
export function usePagination<T>(items: T[], itemsPerPage: number = 25): UsePaginationResult<T> {
    const [currentPage, setCurrentPage] = useState(1);

    // Reset to first page when items change
    useEffect(() => {
        setCurrentPage(1);
    }, [items]);

    // Calculate pagination values
    const totalPages = Math.ceil(items.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = items.slice(startIndex, endIndex);

    return {
        currentPage,
        setCurrentPage,
        currentItems,
        totalPages,
        startIndex,
        endIndex,
        itemsPerPage,
    };
}
